import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Cinema, Showtime } from '../../models';
import { CinemaService } from '../../services/cinema.service';
import { DateChipOption, DateChipRowComponent } from '../../components/date-chip-row.component';
import { MovieShowtimeGroup, MovieShowtimeGroupComponent } from '../../components/movie-showtime-group.component';
import { LocationFilterService } from '../../services/location-filter.service';

@Component({
  selector: 'app-cinema-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, DateChipRowComponent, MovieShowtimeGroupComponent],
  template: `
    <section class="cinema-detail-page">
      <div class="container">
        <div *ngIf="error" class="error-state">{{ error }}</div>
        <div *ngIf="loadingCinema" class="compact-loading"></div>

        <ng-container *ngIf="cinema">
          <div class="cinema-summary">
            <div class="cinema-mark">{{ cinema.chain.slice(0, 2).toUpperCase() }}</div>
            <div>
              <h1>{{ cinema.name }}</h1>
              <div class="summary-meta">
                <span>{{ cinema.city }}, {{ cinema.county }}</span>
                <span *ngIf="distanceLabel()">{{ distanceLabel() }}</span>
                <span>{{ cinema.chain }}</span>
                <span>{{ showtimes.length }} showtime{{ showtimes.length === 1 ? '' : 's' }}</span>
                <a [href]="cinema.showtimesUrl || cinema.websiteUrl" target="_blank" rel="noreferrer">Official times</a>
                <a *ngIf="cinema.websiteUrl" [href]="cinema.websiteUrl" target="_blank" rel="noreferrer">Website</a>
              </div>
            </div>
          </div>

          <div class="detail-search">
            <div class="glass search-panel" role="search">
              <input class="input search-input" [(ngModel)]="query" placeholder="Search movie title" (ngModelChange)="scheduleQueryUpdate()" (keyup.enter)="updateQueryParams()" />
              <div class="filter-line">
                <input class="input" [(ngModel)]="date" type="date" aria-label="Date" (ngModelChange)="updateQueryParams()" />
                <button class="clear-btn" type="button" (click)="clearDate()">Clear</button>
              </div>
            </div>

            <app-date-chip-row [options]="availableDates()" [value]="date" (selected)="selectDate($event)" />
          </div>

          <section class="showtime-results">
            <div class="compact-head">
              <h2>Now showing</h2>
              <span>{{ grouped().length }} movie{{ grouped().length === 1 ? '' : 's' }} · {{ filteredShowtimes().length }} time{{ filteredShowtimes().length === 1 ? '' : 's' }}</span>
            </div>

            <div *ngIf="loadingShowtimes" class="compact-loading"></div>
            <div *ngIf="!loadingShowtimes && !grouped().length" class="empty-state compact-empty">No live showtimes found for this cinema and date.</div>

            <div class="movie-groups" *ngIf="!loadingShowtimes && grouped().length">
              <app-movie-showtime-group *ngFor="let group of grouped()" [group]="group" />
            </div>
          </section>
        </ng-container>
      </div>
    </section>
  `,
  styles: [
    `
      .cinema-detail-page {
        padding: 14px 0 72px;
      }

      .cinema-summary {
        display: grid;
        grid-template-columns: 58px minmax(0, 1fr);
        gap: 12px;
        align-items: center;
        margin-bottom: 10px;
      }

      .cinema-mark {
        width: 58px;
        height: 58px;
        border-radius: 18px;
        display: grid;
        place-items: center;
        color: var(--text);
        font-size: 18px;
        font-weight: 950;
        background: linear-gradient(135deg, var(--red), var(--violet), var(--blue));
        box-shadow: var(--glow);
      }

      h1 {
        margin: 0 0 6px;
        color: var(--text);
        font-size: clamp(24px, 4vw, 38px);
        line-height: 1.05;
        overflow-wrap: anywhere;
      }

      .summary-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 5px;
      }

      .summary-meta span,
      .summary-meta a {
        min-height: 22px;
        border-radius: 999px;
        padding: 4px 7px;
        color: var(--faint);
        background: var(--control-bg);
        font-size: 11px;
        font-weight: 850;
      }

      .summary-meta a {
        color: var(--cyan);
      }

      .detail-search {
        position: sticky;
        top: 68px;
        z-index: 18;
        display: grid;
        gap: 7px;
        padding: 9px 0 8px;
        margin: 0 0 2px;
        background: linear-gradient(180deg, var(--bg) 0%, rgba(7, 9, 20, 0.94) 78%, transparent 100%);
        backdrop-filter: blur(14px);
        min-width: 0;
      }

      .search-panel {
        display: grid;
        grid-template-columns: minmax(250px, 1fr) minmax(250px, 0.55fr);
        gap: 9px;
        padding: 9px;
        border-radius: 17px;
        box-shadow: 0 12px 34px rgba(0, 0, 0, 0.22);
        min-width: 0;
      }

      .search-input {
        font-weight: 850;
      }

      .filter-line {
        display: grid;
        grid-template-columns: minmax(132px, 1fr) auto;
        gap: 8px;
        min-width: 0;
      }

      .search-panel .input {
        min-height: 44px;
        min-width: 0;
      }

      .clear-btn {
        min-height: 44px;
        border: 1px solid var(--line);
        border-radius: 999px;
        padding: 0 13px;
        color: var(--text);
        background: var(--control-bg);
        font-weight: 900;
        cursor: pointer;
      }

      .showtime-results {
        padding-top: 4px;
      }

      .compact-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin: 7px 0 9px;
      }

      .compact-head h2 {
        margin: 0;
        font-size: 18px;
        line-height: 1.1;
      }

      .compact-head span {
        color: var(--muted);
        font-size: 12px;
        font-weight: 900;
        white-space: nowrap;
      }

      .movie-groups {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px;
      }

      .compact-loading {
        min-height: 120px;
        border-radius: 16px;
        background: var(--skeleton);
        background-size: 220% 100%;
        animation: shimmer 1.25s linear infinite;
      }

      .compact-empty {
        padding: 18px 14px;
        border-radius: 16px;
      }

      @media (max-width: 1024px) {
        .detail-search {
          top: 64px;
        }

        .search-panel {
          grid-template-columns: minmax(0, 1fr);
        }
      }

      @media (max-width: 720px) {
        .cinema-detail-page {
          padding-top: 10px;
        }

        .cinema-summary {
          grid-template-columns: 48px minmax(0, 1fr);
          gap: 10px;
          margin-bottom: 4px;
        }

        .cinema-mark {
          width: 48px;
          height: 48px;
          border-radius: 15px;
          font-size: 15px;
        }

        h1 {
          font-size: 21px;
        }

        .summary-meta span:nth-child(n+4),
        .summary-meta a:nth-of-type(n+2) {
          display: none;
        }

        .detail-search {
          padding: 7px 0;
          gap: 6px;
        }

        .search-panel {
          grid-template-columns: minmax(0, 1fr);
          padding: 8px;
          gap: 8px;
          border-radius: 16px;
        }

        .filter-line {
          grid-template-columns: 1fr auto;
        }

        .clear-btn {
          min-width: 58px;
          padding: 0 10px;
        }

        .compact-head {
          margin: 6px 0 8px;
        }

        .compact-head h2 {
          font-size: 17px;
        }

        .compact-head span {
          font-size: 11px;
        }

        .movie-groups {
          grid-template-columns: 1fr;
          gap: 7px;
        }
      }

      @media (max-width: 430px) {
        .clear-btn {
          display: none;
        }
      }
    `,
  ],
})
export class CinemaDetailComponent implements OnInit {
  cinema?: Cinema;
  showtimes: Showtime[] = [];
  dateShowtimes: Showtime[] = [];
  query = '';
  date = '';
  loadingCinema = true;
  loadingShowtimes = true;
  error = '';
  private id = '';
  private queryTimer?: ReturnType<typeof setTimeout>;
  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cinemas: CinemaService,
    private readonly location: LocationFilterService,
  ) {}
  ngOnInit() {
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
    this.cinemas.get(this.id).subscribe({
      next: (cinema) => { this.cinema = cinema; this.loadingCinema = false; },
      error: () => { this.error = 'Could not load this cinema.'; this.loadingCinema = false; },
    });
    this.route.queryParamMap.subscribe((params) => {
      this.query = params.get('query') ?? '';
      this.date = params.get('date') ?? '';
      this.loadAvailableDates();
      this.loadShowtimes();
    });
  }
  loadShowtimes() {
    this.loadingShowtimes = true;
    this.cinemas.showtimes(this.id, { date: this.date }).subscribe({
      next: (rows) => { this.showtimes = rows; this.loadingShowtimes = false; },
      error: () => { this.error = 'Could not load showtimes for this cinema.'; this.loadingShowtimes = false; },
    });
  }
  loadAvailableDates() {
    this.cinemas.showtimes(this.id).subscribe({
      next: (rows) => (this.dateShowtimes = rows),
      error: () => (this.dateShowtimes = []),
    });
  }
  selectDate(value: string) {
    this.date = value;
    this.updateQueryParams();
  }
  clearDate() {
    this.query = '';
    this.date = '';
    this.updateQueryParams();
  }
  scheduleQueryUpdate() {
    if (this.queryTimer) clearTimeout(this.queryTimer);
    this.queryTimer = setTimeout(() => this.updateQueryParams(), 350);
  }
  updateQueryParams() {
    if (this.queryTimer) clearTimeout(this.queryTimer);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        query: this.query.trim() || null,
        date: this.date || null,
        county: null,
        chain: null,
        language: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
  filteredShowtimes(): Showtime[] {
    const term = this.query.trim().toLowerCase();
    if (!term) return this.showtimes;
    return this.showtimes.filter((item) => {
      const haystack = [item.movie?.title, item.movie?.language, item.screenType, item.format]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }
  grouped(): MovieShowtimeGroup[] {
    const map = new Map<string, MovieShowtimeGroup>();
    this.filteredShowtimes().forEach((item) => {
      const key = item.movie?.title ?? 'Unknown movie';
      if (!map.has(key)) {
        map.set(key, {
          movie: key,
          posterUrl: item.movie?.posterUrl,
          language: item.movie?.language,
          runtimeMinutes: item.movie?.runtimeMinutes,
          items: [],
        });
      }
      map.get(key)?.items.push(item);
    });
    return Array.from(map.values()).map((group) => ({
      ...group,
      items: group.items.sort((a, b) => a.showTime.localeCompare(b.showTime)),
    }));
  }
  availableDates(): DateChipOption[] {
    const unique = Array.from(new Set(this.dateShowtimes.map((item) => item.showDate.slice(0, 10)))).sort().slice(0, 10);
    return unique.map((value) => {
      const date = new Date(`${value}T00:00:00`);
      return {
        value,
        label: this.relativeDateLabel(value),
        sublabel: date.toLocaleDateString('en-IE', { day: 'numeric', month: 'short' }),
      };
    });
  }
  initials(title: string): string {
    return title.split(/\s+/).slice(0, 2).map((word) => word[0]).join('').toUpperCase();
  }
  distanceLabel(): string {
    return this.location.formatDistance(this.cinema);
  }
  private relativeDateLabel(value: string): string {
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    const isoToday = today.toISOString().slice(0, 10);
    const isoTomorrow = tomorrow.toISOString().slice(0, 10);
    if (value === isoToday) return 'Today';
    if (value === isoTomorrow) return 'Tomorrow';
    return new Date(`${value}T00:00:00`).toLocaleDateString('en-IE', { weekday: 'short' });
  }
}
